import React from 'react';
import { Grid, Card, CardContent, Typography, Box } from '@mui/material';

/**
 * GridSummaryCards - Displays summary statistics as cards above a grid.
 * Commonly used with useSummaryStats hook.
 *
 * @param {Object} props
 * @param {Array} props.stats - Array of stat objects from useSummaryStats
 * @param {number} [props.columns=6] - Number of grid columns (how many cards fit in a row on lg screens)
 * @param {number} [props.spacing=2] - Spacing between cards
 * @param {Object} [props.sx={}] - Additional sx styles for the container
 * @param {boolean} [props.hideEmpty=false] - Hide cards with zero/empty values
 *
 * @example
 * // With useSummaryStats
 * const { stats } = useSummaryStats({
 *   rowData: comparisonData,
 *   calculations: {
 *     total: { label: 'Total', calculate: (d) => d.length, color: 'primary' },
 *     errors: { label: 'Errors', calculate: countWhere(r => r.error), color: 'error' },
 *   },
 * });
 *
 * <GridSummaryCards stats={stats} columns={6} />
 *
 * @example
 * // Manual stats array
 * <GridSummaryCards
 *   stats={[
 *     { key: 'total', label: 'Total', value: 100, color: 'primary' },
 *     { key: 'active', label: 'Active', value: 85, color: 'success' },
 *   ]}
 * />
 */
export const GridSummaryCards = ({
  stats = [],
  columns = 6,
  spacing = 2,
  sx = {},
  hideEmpty = false,
}) => {
  // Filter out empty stats if hideEmpty is true
  const displayStats = hideEmpty
    ? stats.filter((stat) => {
        const rawValue = stat.rawValue ?? stat.value;
        return rawValue !== 0 && rawValue !== '' && rawValue !== null;
      })
    : stats;

  if (displayStats.length === 0) {
    return null;
  }

  // Calculate responsive grid sizes based on columns count
  // For 6 columns on large screens:
  // xs: 12 (1 card per row)
  // sm: 6 (2 cards per row)
  // md: 12/columns rounded (e.g., 2 for 6 columns)
  const mdSize = Math.max(1, Math.floor(12 / columns));

  return (
    <Grid container spacing={spacing} sx={{ mb: 3, ...sx }}>
      {displayStats.map((stat) => (
        <Grid size={{ xs: 12, sm: 6, md: mdSize }} key={stat.key}>
          <SummaryCard
            label={stat.label}
            value={stat.value}
            color={stat.color}
            icon={stat.icon}
          />
        </Grid>
      ))}
    </Grid>
  );
};

/**
 * Individual summary card component.
 * Can be used standalone or through GridSummaryCards.
 *
 * @param {Object} props
 * @param {string} props.label - Card label/title
 * @param {*} props.value - Value to display
 * @param {string} [props.color='primary'] - MUI color: 'primary', 'secondary', 'error', 'warning', 'info', 'success', or custom
 * @param {React.ReactNode} [props.icon] - Optional icon to display
 * @param {Object} [props.sx={}] - Additional sx styles for the card
 */
export const SummaryCard = ({
  label,
  value,
  color = 'primary',
  icon,
  sx = {},
}) => {
  // Handle custom color strings (e.g., '#ad1457') vs MUI palette colors
  const isCustomColor = color.startsWith('#') || color.startsWith('rgb');
  const colorValue = isCustomColor ? color : `${color}.main`;

  return (
    <Card sx={sx}>
      <CardContent sx={{ textAlign: 'center', py: 2 }}>
        {icon && (
          <Box sx={{ mb: 1, color: colorValue }}>
            {icon}
          </Box>
        )}
        <Typography
          variant="h6"
          sx={{
            color: isCustomColor ? color : colorValue,
            fontWeight: 'bold',
          }}
        >
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
};

/**
 * Horizontal summary stats bar (alternative layout).
 * Shows stats in a single row for compact display.
 *
 * @param {Object} props
 * @param {Array} props.stats - Array of stat objects
 * @param {Object} [props.sx={}] - Additional sx styles
 */
export const SummaryStatsBar = ({ stats = [], sx = {} }) => {
  if (stats.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 3,
        mb: 2,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 1,
        boxShadow: 1,
        ...sx,
      }}
    >
      {stats.map((stat) => {
        const isCustomColor = stat.color?.startsWith('#') || stat.color?.startsWith('rgb');
        const colorValue = isCustomColor ? stat.color : `${stat.color || 'primary'}.main`;

        return (
          <Box key={stat.key} sx={{ textAlign: 'center', minWidth: 80 }}>
            <Typography
              variant="h6"
              sx={{
                color: isCustomColor ? stat.color : colorValue,
                fontWeight: 'bold',
              }}
            >
              {stat.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stat.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * Creates stat card configurations for common grid summary patterns.
 */
export const createCommonStats = {
  /**
   * Total row count stat
   */
  totalCount: (label = 'Total') => ({
    label,
    calculate: (data) => data.length,
    color: 'primary',
  }),

  /**
   * Count of rows matching a status value
   */
  statusCount: (status, label, color = 'info') => ({
    label,
    calculate: (data) => data.filter((row) => row.STATUS === status).length,
    color,
  }),

  /**
   * Count of rows with errors
   */
  errorCount: (label = 'Errors', errorField = 'ERROR') => ({
    label,
    calculate: (data) =>
      data.filter(
        (row) =>
          row[errorField] !== null &&
          row[errorField] !== undefined &&
          row[errorField] !== ''
      ).length,
    color: 'error',
  }),

  /**
   * Average of a numeric field
   */
  average: (field, label, decimals = 2, color = 'secondary') => ({
    label,
    calculate: (data) => {
      const values = data.map((row) => parseFloat(row[field])).filter((v) => !isNaN(v));
      if (values.length === 0) return 0;
      return values.reduce((sum, v) => sum + v, 0) / values.length;
    },
    format: (val) => val.toFixed(decimals),
    color,
  }),

  /**
   * Sum of a numeric field
   */
  sum: (field, label, color = 'primary') => ({
    label,
    calculate: (data) =>
      data.reduce((sum, row) => {
        const value = parseFloat(row[field]);
        return sum + (isNaN(value) ? 0 : value);
      }, 0),
    color,
  }),
};

export default GridSummaryCards;
